import React, { useEffect, useState } from "react";
import Logo from "@/components/Logo";
import { ArrowUpRight } from "lucide-react";
import { useViewAs } from "@/context/ViewAsContext";
import { KlickTippOptin } from "@/components/KlickTippOptin";
import { BRAND } from "@/config/brand";

const LEGAL_LINKS = [
  {
    label: "Impressum",
    href: "https://effektiv-kommunizieren.de/impressum",
    testid: "footer-impressum",
  },
  {
    label: "Datenschutz",
    href: "https://effektiv-kommunizieren.de/datenschutz",
    testid: "footer-datenschutz",
  },
  {
    // Kontakt-Link-Feedback (Do10.09.2026 18:15): auf internes Feedback-
    // Formular geleitet, dort neue Kategorie „Nachricht an Andreas". Mail
    // an hello@aj-kommunikation.de wird beim Absenden automatisch getriggert.
    label: "Kontakt",
    href: "/feedback",
    testid: "footer-kontakt",
  },
];

export default function Footer() {
  const { isDevMode } = useViewAs();
  const [build, setBuild] = useState(null);

  useEffect(() => {
    if (!isDevMode) return;
    fetch("/build_info.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setBuild(d))
      .catch(() => setBuild(null));
  }, [isDevMode]);

  const versionLabel = isDevMode && build
    ? `${build.version} · build ${build.build} · ${build.built_at}`
    : "v2.10.1";

  return (
    <footer
      className="border-t border-slate-100 bg-white py-12 mt-24"
      data-testid="app-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div data-testid="footer-newsletter-block" className="mb-10">
          {/* B.8 Sprint 4c — Newsletter-Box im Footer entfernt (Pro-Solo-Dashboard betroffen) */}
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-3">
            <Logo size={26} />
            <div className="flex items-center gap-2">
              <span
                data-testid="footer-brand-name"
                className="font-semibold tracking-[0.04em] text-[var(--fts-text-muted)] text-xs sm:text-sm"
              >
                {BRAND.name}
              </span>
              <span aria-hidden="true" className="text-slate-300 select-none">·</span>
              <span
                data-testid="footer-brand-marker"
                className="brand-marker font-extrabold tracking-[0.08em] text-[var(--fts-primary-deep)] text-sm sm:text-base uppercase"
              >
                Fokustypen Code
              </span>
            </div>
          </div>

          <nav
            className="flex flex-wrap items-center gap-x-6 gap-y-2 md:gap-x-7"
            data-testid="footer-legal-nav"
          >
            {LEGAL_LINKS.map((link) => {
              // Kontakt-Link-Feedback (Do10.09.2026 18:15): interne Routen
              // (/feedback) sollen in gleicher Tab navigieren; externe URLs
              // wie gehabt in neuer Tab mit Reverse-Tabnabbing-Schutz.
              const isInternal = link.href?.startsWith("/");
              return (
                <a
                  key={link.href}
                  href={link.href}
                  {...(isInternal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                  data-testid={link.testid}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors"
                >
                  {link.label}
                  {!isInternal && (
                    <ArrowUpRight
                      className="w-3.5 h-3.5 opacity-70"
                      aria-hidden="true"
                    />
                  )}
                </a>
              );
            })}
          </nav>
        </div>
        <div
          className="mt-4 pt-4 border-t border-slate-100 text-center text-[10px] text-slate-400 tracking-wide"
          data-testid="footer-version"
        >
          {versionLabel}
        </div>
      </div>
    </footer>
  );
}
